import { z } from 'zod'
import {
  vaultFileSchema,
  vaultFileWriteSchema,
  vaultFileMoveSchema,
  vaultBulkUploadResponseSchema,
  vaultBulkUploadResultItemSchema,
} from '@mindkeep/shared'
import { apiRequest, apiRequestMultipart } from '@/lib/api-client'

export type VaultFile = z.infer<typeof vaultFileSchema>
export type VaultFileWrite = z.infer<typeof vaultFileWriteSchema>
export type VaultFileMove = z.infer<typeof vaultFileMoveSchema>
export type VaultBulkUploadResponse = z.infer<typeof vaultBulkUploadResponseSchema>
export type VaultBulkUploadResultItem = z.infer<typeof vaultBulkUploadResultItemSchema>

export const vaultListEntrySchema = z.object({
  name: z.string(),
  path: z.string(),
  type: z.enum(['file', 'directory']),
  // Imported/generated rows may have no recorded size. Keep it unknown;
  // a null size must not reject the entire source list.
  size_bytes: z.number().nullish().transform((value) => value ?? undefined),
  updated_at: z.string().optional(),
  tags: z.array(z.string()).optional(),
})
export type VaultListEntry = z.infer<typeof vaultListEntrySchema>
export type VaultBulkConflictStrategy = 'skip' | 'overwrite' | 'rename'

const vaultListResponseSchema = z.object({
  entries: z.array(vaultListEntrySchema),
  path: z.string(),
})
export type VaultListResponse = z.infer<typeof vaultListResponseSchema>

const deleteResponseSchema = z.object({ success: z.literal(true) })

export const ingestionJobSchema = z.object({
  id: z.string(),
  vault_file_path: z.string(),
  vault_file_name: z.string(),
  status: z.enum(['pending', 'processing', 'completed', 'completed_with_warnings', 'failed', 'cancelled']),
  result: z.unknown().nullable().optional(),
  error: z.string().nullable().optional(),
  // M2-A: durable Retry-Felder
  last_error: z.string().nullable().optional(),
  attempts: z.number().int().nullable().optional(),
  max_attempts: z.number().int().nullable().optional(),
  next_retry_at: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  created_at: z.string(),
  completed_at: z.string().nullable().optional(),
})
export type IngestionJob = z.infer<typeof ingestionJobSchema>

const ingestionJobsResponseSchema = z.object({
  jobs: z.array(ingestionJobSchema),
})

const retryIngestionJobResponseSchema = z.object({
  ok: z.boolean(),
  jobId: z.string(),
})

export const reparseVaultFileResponseSchema = z.object({
  ok: z.literal(true),
  path: z.string(),
  parserError: z.string().nullable(),
  ingestion: z
    .union([z.object({ jobId: z.string() }), z.object({ error: z.string() })])
    .nullable(),
})
export type ReparseVaultFileResponse = z.infer<typeof reparseVaultFileResponseSchema>

export function listVaultFiles() {
  return apiRequest({ path: '/vault/files', schema: vaultListResponseSchema })
}

export function getVaultFile(path: string) {
  return apiRequest({
    path: `/vault/file?path=${encodeURIComponent(path)}`,
    schema: vaultFileSchema,
  })
}

export function putVaultFile(body: VaultFileWrite) {
  return apiRequest({
    method: 'PUT',
    path: '/vault/file',
    body,
    schema: vaultFileSchema,
  })
}

export function deleteVaultFile(path: string) {
  return apiRequest({
    method: 'DELETE',
    path: `/vault/file?path=${encodeURIComponent(path)}`,
    schema: deleteResponseSchema,
  })
}

export function moveVaultFile(body: VaultFileMove) {
  return apiRequest({
    method: 'POST',
    path: '/vault/move',
    body,
    schema: vaultFileSchema,
  })
}

export function bulkUploadVaultFiles(opts: {
  files: File[]
  targetFolder: string
  conflictStrategy: VaultBulkConflictStrategy
}) {
  const formData = new FormData()
  formData.set('targetFolder', opts.targetFolder)
  formData.set('conflictStrategy', opts.conflictStrategy)
  for (const file of opts.files) formData.append('files', file)
  return apiRequestMultipart({
    method: 'POST',
    path: '/vault/bulk-upload',
    formData,
    schema: vaultBulkUploadResponseSchema,
  })
}

export function listIngestionJobs() {
  return apiRequest({
    path: '/vault/ingestion-jobs',
    schema: ingestionJobsResponseSchema,
  })
}

export function retryIngestionJob(jobId: string) {
  return apiRequest({
    method: 'POST',
    path: `/vault/ingestion-jobs/${jobId}/retry`,
    schema: retryIngestionJobResponseSchema,
  })
}

export function reparseVaultFile(path: string) {
  return apiRequest({
    method: 'POST',
    path: '/vault/reparse',
    body: { path },
    schema: reparseVaultFileResponseSchema,
  })
}
